import React, { Component } from 'react';
import { Button, Input, Select, Responsive } from 'common/components';
import {
  FIELD_SIZE,
  ANALYTICS,
  ROUTE_FIELDS_DETAIL,
  BANNER_IDS,
} from 'common/constants';
import { number, object, array, func, string, bool } from 'prop-types';
import { reduxForm } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { base64url_encode } from 'util/base64';
import mixpanel from 'mixpanel-browser';
import get from 'lodash.get';
import {
  FETCH_ALL_FIELDS,
  MUTATION_CREATE_FIELD,
  MUTATION_UPDATE_FIELD,
  MUTATION_RENAME_FIELD,
} from 'api/field';
import {
  maxValue,
  minValuePlanted,
  minValueField,
  required,
  maxLength,
  numberOnly,
  notOneOf,
} from 'common/form-validators';
import * as normalizers from 'common/normalizers';
import { toCommaNumber } from 'common/formatters';
import { graphQlToFormErrors } from 'common/utils';

export class FieldForm extends Component {
  static displayName = 'FieldForm';

  static propTypes = {
    size: number,
    coordinates: string.isRequired,
    currentField: object,
    existingFieldNames: array,
    onSubmit: func.isRequired,
    onCancel: func.isRequired,
    formTitle: string,
    error: string,
    handleSubmit: func.isRequired,
    valid: bool.isRequired,
    pristine: bool.isRequired,
    submitting: bool.isRequired,
    id: string,
    used: number,
    client: object,
  };

  render() {
    const {
      onSubmit,
      onCancel,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      formTitle = 'Setup Field',
      id,
    } = this.props;

    const isEditMode = !!id;
    const validators = isEditMode
      ? [this.farmSizeNotZero, this.farmSizeBiggerThanPlanted, this.farmSizeMax]
      : [this.farmSizeNotZero, this.farmSizeMax];

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <small className={'text--info'}>* Required</small>
        <h2 className={'text--header'}>{formTitle}</h2>

        <Input
          name="name"
          disabled={submitting}
          title="Field Name"
          validate={[required, this.nameMax, this.farmNameTaken]}
          validateOnChange={true}
          normalizeOnBlur={normalizers.trim}
        />

        {/* size need validation on load, since the size is initially coming from polygon */}
        <Input
          name="size"
          disabled={submitting}
          title="Field Size"
          infoText="Acres"
          validate={[required, numberOnly, ...validators]}
          normalize={this.normalizeFarmSize}
          format={toCommaNumber}
          validateOnChange={true}
          validateInit={true}
        />

        <Input
          name="coordinates"
          disabled={submitting}
          title="Location"
          readOnly={true}
          validate={[required]}
        />
        <Responsive.Desktop>
          <small>Move the pin on the map to readjust coordinates</small>
        </Responsive.Desktop>
        <Responsive.Mobile>
          <small>Use desktop to adjust the field coordinates</small>
        </Responsive.Mobile>

        <Input
          name="identification"
          disabled={submitting}
          title="Legal Land Name"
          normalizeOnBlur={normalizers.trim}
        />

        <Select
          name="ownership"
          disabled={submitting}
          title="Ownership"
          disablePromptOption
          options={[
            { label: 'Own', value: 'own' },
            { label: 'Rent', value: 'rent' },
            { label: 'Crop Share', value: 'crop_share' },
          ]}
        />

        <Input
          name="notes"
          disabled={submitting}
          title="Notes"
          validate={[this.notesMax]}
          normalizeOnBlur={normalizers.trim}
        />

        {error && <p className="text--error">{error}</p>}

        <div className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Save
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="button"
              className="btn"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </div>
      </form>
    );
  }

  // validations
  nameMax = value => maxLength(value, FIELD_SIZE.MAX_TITLE);
  notesMax = value => maxLength(value, FIELD_SIZE.MAX_NOTES);
  farmSizeMax = value => maxValue(value, FIELD_SIZE.MAX);
  farmSizeNotZero = value => minValueField(value, FIELD_SIZE.ZERO);
  farmSizeBiggerThanPlanted = value =>
    minValuePlanted(value, FIELD_SIZE.ZERO + this.props.used);

  // normlizers
  normalizeFarmSize = value =>
    normalizers.limitDigits(normalizers.onlyNumbers(value), 7, 2);

  // BE validation exists as well
  farmNameTaken = value => {
    const { currentField, existingFieldNames } = this.props;
    // if there's no other fields, make this valid by returning undefined
    if (!existingFieldNames) return;
    return (
      ((currentField && value !== currentField.name) || !currentField) &&
      notOneOf(value, existingFieldNames)
    );
  };
}

const mapState = (state, { coordinates, size, currentField }) => ({
  initialValues: {
    coordinates,
    size,
    ownership:
      currentField && currentField.ownership ? currentField.ownership : 'own',
    name: currentField && currentField.name,
    notes: currentField && currentField.notes,
    identification: currentField && currentField.identification,
  },
});

const createFieldMutation = graphql(MUTATION_CREATE_FIELD, {
  name: 'createField',
  options: ({ goToDetailPage, triggerBanner }) => ({
    refetchQueries: () => [
      { query: FETCH_ALL_FIELDS, variables: { active: true } },
    ],
    onCompleted: ({ createField }) => {
      if (createField && createField.size) {
        mixpanel.track(ANALYTICS.EVENTS.FIELD.CREATED, {
          [ANALYTICS.PROPERTIES.FIELD.FIELD_SIZE]: createField.size.size,
        });
        mixpanel.people.increment({
          [ANALYTICS.PROPERTIES.FIELD.AREA_MAPPED]: createField.size.size,
        });
      }

      goToDetailPage(
        `${ROUTE_FIELDS_DETAIL}/${base64url_encode(createField.name.trim())}`
      );
      triggerBanner(BANNER_IDS.APP, 'FIELD ADDED', false);
    },
  }),
});

const updateFieldMutation = graphql(MUTATION_UPDATE_FIELD, {
  name: 'updateField',
  options: ({ goToNewFieldPage, triggerBanner }) => ({
    refetchQueries: () => [
      { query: FETCH_ALL_FIELDS, variables: { active: true } },
    ],
    onCompleted: ({ updateField }) => {
      goToNewFieldPage(updateField);
      triggerBanner(BANNER_IDS.APP, 'FIELD UPDATED', false);
    },
  }),
});

const renameFieldMutation = graphql(MUTATION_RENAME_FIELD, {
  name: 'renameField',
});

const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    const {
      id,
      createField,
      updateField,
      renameField,
      location,
      shape,
      currentField,
    } = props;
    const { name, identification, notes, ownership, size } = values;
    // eslint-disable-next-line no-unused-vars
    const omitTypename = ({ __typename, ...rest }) => ({ ...rest });

    // update field
    if (id) {
      const fieldUpdateInput = {
        identification: identification || '',
        location: omitTypename(location),
        notes,
        ownership,
        shape: shape.map(omitTypename),
        size: { size, type: 'acres' },
      };

      // update name first if name changes
      // because id is based on name
      let renameResult;
      if (currentField.name !== name) {
        renameResult = await renameField({
          variables: { id, newName: name.trim() },
        }).catch(graphQlToFormErrors);
      }

      let newId = get(renameResult, 'data.renameField.id', id);
      return updateField({
        variables: {
          id: newId,
          fieldUpdateInput,
        },
      }).catch(graphQlToFormErrors);
    }

    // create field
    const fieldCreateInput = {
      name: name.trim(),
      identification: identification || '',
      location,
      notes,
      ownership,
      shape,
      size: { size, type: 'acres' },
    };

    return createField({
      variables: { fieldCreateInput },
    }).catch(graphQlToFormErrors);
  },
});

const enhance = compose(
  createFieldMutation,
  updateFieldMutation,
  renameFieldMutation,
  connect(
    mapState,
    mapDispatch
  ),
  reduxForm({
    form: 'fieldform',
    /**
     * set enableReinitialize from true to false(default)
     *
     * When enableReinitialize set to true, the form will reinitialize every time the initialValues prop changes. Defaults to false.
     */
    enableReinitialize: false,
    keepDirtyOnReinitialize: true,
  })
);

export default enhance(FieldForm);



// WEBPACK FOOTER //
// ./src/containers/FieldForm/index.js